import React from 'react';

import { Checkbox } from '@mui/material';

import { Badge } from '../common';
import { EllipsisText } from '../../EllipsisText';
import { Box, BoxColors } from '../../Box';
import { Typography } from '../../Typography';
import { Tooltip } from '../../Tooltip';

import { Option } from './dropdownOption.types';

interface ExtendedOption extends Option {
  subtitle?: string;
  startIcon?: JSX.Element;
}

const COLORS = {
  gray: 'stroke.primary',
  darkgray: 'stroke.secondary',
  green: 'accent.stroke.success',
  orange: 'accent.stroke.warning',
  purple: 'accent.stroke.review',
  blue: 'accent.stroke.blue',
  red: 'accent.stroke.error',
};

/**
 * Функция renderTwoLineOption рендерит опцию с двумя строками.
 * Опция может содержать подзаголовок и значок (badge).
 *
 * @param {Option} option - Объект опции, содержащий label и badge.
 * @returns {JSX.Element} Компонент опции с двумя строками.
 *
 * @example
 * <Box>
 *   {renderTwoLineOption({ label: 'Заголовок, Подзаголовок', badge: { title: 'Info', description: 'Описание', color: 'primary', position: 'top' } })}
 * </Box>
 */

export const renderTwoLineOption = (option: Option) => {
  const [subtitle, titleFirst, titleSecond] = option.label.split(', ');
  const { badge } = option;

  const isBottom = badge?.position === 'bottom';

  let title = '';

  if (subtitle && !titleFirst) {
    title = subtitle;
  } else {
    const titleSecondFormatted = titleSecond ? `, ${titleSecond}` : '';
    title = `${titleFirst || ''}${titleSecondFormatted}`;
  }

  return (
    <Box py={badge ? '8' : '12'}>
      {title && (
        <Box>
          <Typography variant="body3">{title}</Typography>
        </Box>
      )}
      <Box
        display="flex"
        alignItems="center"
        flexDirection="row"
        gap="8"
        mt="4"
      >
        {badge && (
          <Box>
            <Badge
              title={badge.title}
              description={badge.description}
              color={badge.color}
            />
          </Box>
        )}
        {title !== subtitle && (
          <Box order={isBottom ? 0 : 1}>
            <Typography
              component="div"
              variant={title ? 'caption' : 'body3'}
              color="text.light.tertirary"
            >
              {subtitle}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

/**
 * Функция renderOptionWithBadge рендерит опцию с значком (badge).
 *
 * @param {Option} option - Объект опции, содержащий label и badge.
 * @returns {JSX.Element} Компонент опции с значком.
 *
 * @example
 * <Box>
 *   {renderOptionWithBadge({ label: 'Заголовок', badge: { title: 'Info', description: 'Описание', color: 'primary' } })}
 * </Box>
 */
export const renderOptionWithBadge = (option: Option) => {
  const { badge, label } = option;

  return (
    <Box py={badge ? '8' : '12'}>
      <Box
        display="flex"
        alignItems="center"
        flexDirection="row"
        gap="8"
        mt="4"
      >
        {badge && (
          <Box>
            <Badge
              title={badge.title}
              description={badge.description}
              color={badge.color}
            />
          </Box>
        )}
        <Box>
          <Typography
            component="div"
            variant="body3"
            color="text.light.primary"
          >
            {label}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

/**
 * Функция renderTwoLineOptionEllipsis рендерит опцию с двумя строками,
 * где первая строка содержит основной заголовок, а вторая строка содержит
 * подзаголовок, обрезанный с помощью EllipsisText.
 *
 * @param {Option} option - Объект опции, содержащий label.
 * @returns {JSX.Element} Компонент опции с двумя строками и обрезанным подзаголовком.
 *
 * @example
 * <Box>
 *   {renderTwoLineOptionEllipsis({ label: 'Заголовок, Подзаголовок, Дополнительный текст' })}
 * </Box>
 */
export const renderTwoLineOptionEllipsis = (option: Option) => {
  const [title, ...rest] = option.label.split(',');

  return (
    <Box py="12">
      <Box>
        <Typography variant="body3">{title}</Typography>
      </Box>
      <Box mt="2">
        <Typography variant="caption" color="text.light.tertirary">
          <EllipsisText>{rest?.join(', ')}</EllipsisText>
        </Typography>
      </Box>
    </Box>
  );
};

/**
 * Функция renderTwoLineWithJoinedTitleAndSubtitle рендерит опцию с двумя строками,
 * где первая строка содержит основной заголовок + подзаголовок, объединенные через запятую.
 *
 * @param {Option} option - Объект опции, содержащий label.
 * @returns {JSX.Element} Компонент опции с двумя строками и обрезанным подзаголовком.
 *
 * @example
 * <Box>
 *   {renderTwoLineWithJoinedTitleAndSubtitle({ label: 'Заголовок, Подзаголовок, Дополнительный текст' })}
 * </Box>
 */
export const renderTwoLineWithJoinedTitleAndSubtitle = (option: Option) => {
  const [title, subtitle, ...rest] = option.label.split(',');

  return (
    <Box py="12">
      <Box>
        <Typography variant="body3">{[title, subtitle].join(', ')}</Typography>
      </Box>
      <Box mt="2">
        <Typography variant="caption" color="text.light.tertirary">
          <EllipsisText>{rest?.join(', ')}</EllipsisText>
        </Typography>
      </Box>
    </Box>
  );
};

/**
 * Функция renderThreeOrTwoLineOptionEllipsis рендерит опцию с двумя строками,
 * где первая строка содержит основной заголовок, а вторая строка содержит
 * подзаголовок, обрезанный с помощью EllipsisText.
 *
 * @param {Option} option - Объект опции, содержащий label.
 * @returns {JSX.Element} Компонент опции с тремя строками и обрезанным подзаголовком.
 *
 * @example
 * <Box>
 *   {renderTwoOrThreeLineOptionEllipsis({ label: 'Заголовок, Подзаголовок, Дополнительный текст' })}
 * </Box>
 */

export const renderTwoOrThreeLineOptionEllipsis = (option: Option) => {
  const [title, subtitleFirst, subtitleSecond] = option.label.split('\n');

  return (
    <Box py="12">
      <Typography variant="body3">{title}</Typography>

      <Box mt="4">
        <Typography variant="caption" color="text.light.tertirary">
          <EllipsisText>{subtitleFirst}</EllipsisText>
          {subtitleSecond && <EllipsisText>{subtitleSecond}</EllipsisText>}
        </Typography>
      </Box>
    </Box>
  );
};

/**
 * Функция renderLineOptionWithTooltip рендерит опцию с подсказкой (tooltip).
 * Основной текст опции обрезается с помощью EllipsisText.
 *
 * @param {Option} option - Объект опции, содержащий label.
 * @returns {JSX.Element} Компонент опции с подсказкой.
 *
 * @example
 * <Box>
 *   {renderLineOptionWithTooltip({ label: 'Длинный заголовок, который нужно обрезать' })}
 * </Box>
 */
export const renderLineOptionWithTooltip = (option: Option) => {
  const { label } = option;

  return (
    <Tooltip title={label} placement="right">
      <Box py="12">
        <Box py="2">
          <Typography variant="body3">
            <EllipsisText rows={1}>{label}</EllipsisText>
          </Typography>
        </Box>
      </Box>
    </Tooltip>
  );
};

/**
 * Функция renderLineOptionWithDisabledItemAndTooltip рендерит опцию с подсказкой (tooltip),
 * если опция отключена. Иначе рендерит обычную опцию.
 *
 * @param {Option} option - Объект опции, содержащий label, disabled и tooltipText.
 * @returns {JSX.Element} Компонент опции с подсказкой, если она отключена, иначе обычная опция.
 *
 * @example
 * <Box>
 *   {renderLineOptionWithDisabledItemAndTooltip({ label: 'Отключенная опция', disabled: true, tooltipText: 'Эта опция недоступна' })}
 * </Box>
 */
export const renderLineOptionWithDisabledItemAndTooltip = (option: Option) => {
  const { disabled, label, tooltipText } = option;

  if (disabled && tooltipText) {
    return (
      <Box>
        <Tooltip title={tooltipText} placement="right">
          <Box py="12" px="16" display="inline-block" cursor="pointer">
            <Typography variant="body3" color="text.light.tertirary">
              <EllipsisText rows={1}>{label}</EllipsisText>
            </Typography>
          </Box>
        </Tooltip>
      </Box>
    );
  } else {
    return (
      <Box py="8">
        <Typography variant="body3">{label}</Typography>
      </Box>
    );
  }
};

/**
 * Функция renderTwoLineOptionWithSeparateSubtitle рендерит опцию с двумя строками,
 * где первая строка содержит основной заголовок (label), а вторая строка содержит
 * отдельный подзаголовок (subtitle).
 *
 * @param {ExtendedOption} option - Объект опции, содержащий label и subtitle.
 * @returns {JSX.Element} Компонент опции с двумя строками и отдельным подзаголовком.
 *
 * @example
 * <Box>
 *   {renderTwoLineOptionWithSeparateSubtitle({ label: 'Заголовок', subtitle: 'Подзаголовок' })}
 * </Box>
 */
export const renderTwoLineOptionWithSeparateSubtitle = (
  option: ExtendedOption,
) => {
  const { subtitle, label } = option;

  return (
    <Box py="12">
      <Box>
        <Typography variant="body3">{label}</Typography>
      </Box>
      <Box
        display="flex"
        alignItems="center"
        flexDirection="row"
        gap="8"
        mt="4"
      >
        <Box>
          <Typography
            component="div"
            variant="caption"
            color="text.light.tertirary"
          >
            {subtitle}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

/**
 * Функция renderTwoLineOptionWithJSXLabelAndSubtitle рендерит опцию с двумя строками,
 * где первая строка может быть JSX-элементом или строкой (label), а вторая строка —
 * отдельный подзаголовок (subtitle).
 *
 * @param {React.ReactNode | string} label - основной текст.
 * @param {string} subtitle - подзаголовок.
 * @returns {React.ReactNode} Компонент опции с JSX-заголовком и отдельным подзаголовком.
 *
 * @example
 * <Box>
 *   {renderTwoLineOptionWithJSXLabelAndSubtitle({
 *     label: <Typography variant="body3" color="text.light.primary">Кастомный заголовок</Typography>,
 *     subtitle: 'Подзаголовок'
 *   })}
 * </Box>
 */
export const renderTwoLineOptionWithNodeLabelAndSubtitle = ({
  label,
  subtitle,
}: {
  label: React.ReactNode | string;
  subtitle?: React.ReactNode | string;
}) => (
  <Box display="flex" flexDirection="column">
    <Box>{label}</Box>
    <Typography variant="caption" color="text.light.tertirary">
      {subtitle}
    </Typography>
  </Box>
);

/**
 * Функция renderOptionWithColoredIndicator рендерит опцию с цветовым индикатором.
 *
 * @param {ExtendedOption} option - Объект опции, содержащий color.
 * @returns {JSX.Element} Компонент опции с цветовым индикатором.
 *
 * @example
 * <Box>
 *   {renderOptionWithColoredIndicator({ label: 'Заголовок', color: 'green' })}
 * </Box>
 */
export const renderOptionWithColoredIndicator = (option: ExtendedOption) => {
  const { label, color } = option;

  return (
    <Box display="flex" alignItems="center" gap="12" py="12">
      {color && (
        <Box
          width={8}
          height={8}
          bgcolor={COLORS[color] as BoxColors}
          radius="round"
        />
      )}
      <Box>
        <Typography variant="body3">{label}</Typography>
      </Box>
    </Box>
  );
};

/**
 * Функция renderCustomOptionWithHighlight рендерит опцию с подсветкой выбранного элемента
 *
 * @param {Option} option - Объект опции, содержащий value и label
 * @param {string} currentValue - Текущее выбранное значение (для сравнения)
 * @returns {JSX.Element} Компонент опции с подсветкой выбранного элемента
 *
 * @example
 * <Box>
 *   {renderCustomOptionWithHighlight({ value: '1', label: 'Option 1' }, '1')}
 * </Box>
 */
export const renderCustomOptionWithHighlight = (
  option: Option,
  currentValue?: string,
) => {
  return (
    <Box display="flex" alignItems="start" gap="12" py="8">
      <Typography
        variant="body3"
        color={
          option.value === currentValue
            ? 'accent.text.blue'
            : 'text.light.primary'
        }
      >
        {option.label}
      </Typography>
    </Box>
  );
};

export const renderTwoLineOptionWithSubtitleAndCheckbox = (
  option: Option,
  value: string[] | undefined,
) => {
  const [titleFirst, subtitle, ...rest] = option.label.split(', ');

  const isDisabled = option.disabled;
  const tooltipText = option.tooltipText;

  const title = titleFirst;

  const formattedSubtitle = `${subtitle || ''}${
    !!rest.length ? `, ${rest.join(', ')}` : ''
  }`;

  const handleStopPropagination = (event: React.MouseEvent<HTMLElement>) =>
    event?.stopPropagation();

  if (isDisabled) {
    return (
      <Box
        py="12"
        display="flex"
        alignItems="center"
        onClick={handleStopPropagination}
      >
        <Checkbox
          checked={
            !!value && value.some((item: string) => item === option.value)
          }
          color="primary"
        />
        <Tooltip title={tooltipText as string} placement="right">
          <Box display="flex" flexDirection="column">
            {title && (
              <Box>
                <Typography variant="body3">{title}</Typography>
              </Box>
            )}
            <Box
              display="flex"
              alignItems="center"
              flexDirection="row"
              gap="8"
              mt="4"
            >
              {title !== subtitle && (
                <Box order={1}>
                  <Typography
                    component="div"
                    variant={title ? 'caption' : 'body3'}
                    color="text.light.tertirary"
                  >
                    {formattedSubtitle}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Tooltip>
      </Box>
    );
  }

  return (
    <Box py="12" display="flex" alignItems="center">
      <Box>
        <Checkbox
          checked={
            !!value && value.some((item: string) => item === option.value)
          }
          color="primary"
        />
      </Box>
      <Box display="flex" flexDirection="column">
        {title && (
          <Box>
            <Typography variant="body3">{title}</Typography>
          </Box>
        )}
        <Box
          display="flex"
          alignItems="center"
          flexDirection="row"
          gap="8"
          mt="4"
        >
          {title !== subtitle && (
            <Box order={1}>
              <Typography
                component="div"
                variant={title ? 'caption' : 'body3'}
                color="text.light.tertirary"
              >
                {formattedSubtitle}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export const renderTwoLineOptionWithCheckbox = (
  option: Option,
  value: string[] | undefined,
) => {
  const [titleFirst, titleSecond, subtitle, ...rest] = option.label.split(', ');

  const isDisabled = option.disabled;
  const tooltipText = option.tooltipText;

  let title = '';

  if (subtitle && !titleFirst) {
    title = subtitle;
  } else {
    const titleSecondFormatted = titleSecond ? `, ${titleSecond}` : '';
    title = `${titleFirst || ''}${titleSecondFormatted}`;
  }

  const formattedSubtitle = `${subtitle}${
    !!rest.length ? `, ${rest.join(', ')}` : ''
  }`;

  const handleStopPropagination = (event: React.MouseEvent<HTMLElement>) =>
    event?.stopPropagation();

  if (isDisabled) {
    return (
      <Box
        py="12"
        display="flex"
        alignItems="center"
        onClick={handleStopPropagination}
      >
        <Checkbox
          checked={
            !!value && value.some((item: string) => item === option.value)
          }
          color="primary"
        />
        <Tooltip title={tooltipText as string} placement="right">
          <Box display="flex" flexDirection="column">
            {title && (
              <Box>
                <Typography variant="body3">{title}</Typography>
              </Box>
            )}
            <Box
              display="flex"
              alignItems="center"
              flexDirection="row"
              gap="8"
              mt="4"
            >
              {title !== subtitle && (
                <Box order={1}>
                  <Typography
                    component="div"
                    variant={title ? 'caption' : 'body3'}
                    color="text.light.tertirary"
                  >
                    {formattedSubtitle}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Tooltip>
      </Box>
    );
  }

  return (
    <Box py="12" display="flex" alignItems="center">
      <Box>
        <Checkbox
          checked={
            !!value && value.some((item: string) => item === option.value)
          }
          color="primary"
        />
      </Box>
      <Box display="flex" flexDirection="column">
        {title && (
          <Box>
            <Typography variant="body3">{title}</Typography>
          </Box>
        )}
        {subtitle && (
          <Box
            display="flex"
            alignItems="center"
            flexDirection="row"
            gap="8"
            mt="4"
          >
            {title !== subtitle && (
              <Box order={1}>
                <Typography
                  component="div"
                  variant={title ? 'caption' : 'body3'}
                  color="text.light.tertirary"
                >
                  {formattedSubtitle}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

/**
 * Функция renderOptionWithIcon рендерит опцию с иконкой.
 *
 * @param {ExtendedOption} option - Объект опции, содержащий icon.
 * @returns {JSX.Element} Компонент опции с иконкой.
 *
 * @example
 * <Box>
 *   {renderOptionWithIcon({ label: 'Заголовок', })}
 * </Box>
 */
export const renderOptionWithIcon = (option: ExtendedOption) => {
  const { label, startIcon } = option;

  return (
    <Box display="flex" alignItems="center" gap="12" py="12">
      {startIcon && startIcon}
      <Box>
        <Typography variant="body3">{label}</Typography>
      </Box>
    </Box>
  );
};
